import { Link } from "react-router-dom";
import { ArrowUpRight, Truck, Container, Package, ShieldCheck, Globe2, Boxes, Warehouse, Layers } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";

const SPECIALIZED_IMG = "/load-1.webp";
const FLATBED_IMG = "/truck-deli-2.webp";
const LTL_IMG = "/truck-deli-1.webp";
const CROSSBORDER_IMG = "/cross-border-load.webp";
const INTL_IMG = "https://images.unsplash.com/photo-1758745791998-11ea5eb5df40?auto=format&fit=crop&w=900&q=70&fm=webp";
const DRYVAN_IMG = "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=900&q=70&fm=webp";
const CONESTOGA_IMG = "https://images.pexels.com/photos/2199293/pexels-photo-2199293.jpeg?auto=compress&cs=tinysrgb&w=900";
const WAREHOUSE_IMG = "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=900&q=70&fm=webp";

export default function ServicesGrid() {
  const { t } = useT();
  return (
    <section data-testid="services-section" id="services" className="section-pad bg-light">
      <div className="container-mdgx">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="text-overline mb-4">{t("services.overline")}</div>
            <h2 className="section-headline text-4xl md:text-6xl">
              {t("services.headline")}
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="leading-relaxed text-neutral-700">
              {t("services.intro")}
            </p>
          </div>
        </div>

        {/* Tetris grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Specialized — large feature */}
          <div data-testid="service-card-specialized" className="md:col-span-7 card-tech overflow-hidden group relative min-h-[420px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${SPECIALIZED_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-between p-8">
              <div className="flex items-center gap-3 self-start border border-white/20 px-3 py-1.5 backdrop-blur-md bg-black/40">
                <ShieldCheck size={14} className="text-[#D9633F]" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-white">{t("services.flagship")}</span>
              </div>
              <div>
                <h3 className="font-display font-black text-white text-3xl md:text-4xl tracking-tight">{t("services.specialized.title")}</h3>
                <p className="text-white/70 mt-3 max-w-md">
                  {t("services.specialized.desc")}
                </p>
                <Link to="/quote" className="inline-flex items-center gap-2 mt-5 text-sm text-[#D9633F] font-semibold uppercase tracking-[0.2em] hover:gap-3 transition-all">
                  {t("services.specialized.cta")} <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </div>

          {/* Flatbed */}
          <div data-testid="service-card-flatbed" className="md:col-span-5 card-tech overflow-hidden group relative min-h-[420px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${FLATBED_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="relative z-10 h-full flex flex-col justify-end p-8">
              <Truck className="text-[#D9633F] mb-4" size={28} />
              <h3 className="font-display font-bold text-white text-2xl md:text-3xl">{t("services.flatbed.title")}</h3>
              <p className="text-white/70 mt-2">{t("services.flatbed.desc")}</p>
            </div>
          </div>

          {/* LTL */}
          <div data-testid="service-card-ltl" className="md:col-span-4 card-tech overflow-hidden group relative min-h-[360px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${LTL_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7">
              <Boxes className="text-[#D9633F] mb-3" size={26} />
              <h3 className="font-display font-bold text-white text-2xl">{t("services.ltl.title")}</h3>
              <p className="text-white/70 mt-2 text-sm">{t("services.ltl.desc")}</p>
            </div>
          </div>

          {/* Cross-border — real Midemide flatbed load (Cambridge crates) */}
          <div data-testid="service-card-crossborder" className="md:col-span-4 card-tech overflow-hidden group relative min-h-[360px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${CROSSBORDER_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/25" />
            <div className="relative z-10 h-full flex flex-col justify-between p-7">
              <Globe2 className="text-[#D9633F]" size={28} />
              <div>
                <h3 className="font-display font-bold text-white text-2xl">{t("services.crossborder.title")}</h3>
                <p className="text-white/80 mt-3 text-sm leading-relaxed">
                  {t("services.crossborder.desc")}
                </p>
                <div className="mt-5 flex gap-2 flex-wrap">
                  {["PARS", "PAPS", "ACE", "ACI"].map((b) => (
                    <span key={b} className="font-mono text-[10px] tracking-wider px-2 py-1 border border-white/30 text-white/90 bg-black/40 backdrop-blur-sm">{b}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* International containers */}
          <div data-testid="service-card-international" className="md:col-span-4 card-tech overflow-hidden group relative min-h-[360px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${INTL_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7">
              <Container className="text-[#D9633F] mb-3" size={26} />
              <h3 className="font-display font-bold text-white text-2xl">{t("services.international.title")}</h3>
              <p className="text-white/70 mt-2 text-sm">{t("services.international.desc")}</p>
            </div>
          </div>

          {/* Small package */}
          <div data-testid="service-card-smallpkg" className="md:col-span-12 card-tech p-8 md:p-10 flex flex-col md:flex-row gap-8 md:items-center justify-between">
            <div className="flex items-center gap-6">
              <Package className="text-[#D9633F]" size={36} />
              <div>
                <h3 className="font-display font-bold text-white text-2xl md:text-3xl">{t("services.smallpkg.title")}</h3>
                <p className="text-white/70 mt-1">{t("services.smallpkg.desc")}</p>
              </div>
            </div>
            <Link to="/quote" className="btn-sharp btn-ghost shrink-0">
              {t("services.smallpkg.cta")} <ArrowUpRight size={14} />
            </Link>
          </div>

          {/* Dry Van */}
          <div data-testid="service-card-dryvan" className="md:col-span-4 card-tech overflow-hidden group relative min-h-[360px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${DRYVAN_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7">
              <Truck className="text-[#D9633F] mb-3" size={26} />
              <h3 className="font-display font-bold text-white text-2xl">{t("services.dryvan.title")}</h3>
              <p className="text-white/70 mt-2 text-sm">{t("services.dryvan.desc")}</p>
            </div>
          </div>

          {/* Conestoga */}
          <div data-testid="service-card-conestoga" className="md:col-span-4 card-tech overflow-hidden group relative min-h-[360px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${CONESTOGA_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7">
              <Layers className="text-[#D9633F] mb-3" size={26} />
              <h3 className="font-display font-bold text-white text-2xl">{t("services.conestoga.title")}</h3>
              <p className="text-white/70 mt-2 text-sm">{t("services.conestoga.desc")}</p>
            </div>
          </div>

          {/* 3PL Warehousing */}
          <div data-testid="service-card-3pl" className="md:col-span-4 card-tech overflow-hidden group relative min-h-[360px]">
            <div className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700" style={{ backgroundImage: `url(${WAREHOUSE_IMG})` }} />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
            <div className="relative z-10 h-full flex flex-col justify-end p-7">
              <Warehouse className="text-[#D9633F] mb-3" size={26} />
              <h3 className="font-display font-bold text-white text-2xl">{t("services.warehouse.title")}</h3>
              <p className="text-white/70 mt-2 text-sm">{t("services.warehouse.desc")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
